import { useState } from "react";
import {
  Users,
  Gift,
  Copy,
  CheckCircle,
  Loader2,
  Link,
  DollarSign,
  Share2,
  BarChart3,
  MousePointerClick,
  TrendingUp,
  Wallet,
  Clock,
} from "lucide-react";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Affiliation() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [copied, setCopied] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/affiliates/register`, {
        name: name.trim(),
        email: email.trim(),
      });
      setAffiliate(response.data.affiliate);
      toast.success(response.data.message);
      // Auto-load analytics
      fetchAnalytics(response.data.affiliate.code);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchAnalytics = async (code) => {
    if (!code) return;
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API}/affiliates/${code}/analytics`);
      setAnalytics(res.data);
    } catch {
      // Analytics may be empty, that's fine
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const copyLink = () => {
    if (!affiliate?.referral_link) return;
    navigator.clipboard.writeText(affiliate.referral_link);
    setCopied(true);
    toast.success("Lien copié !");
    setTimeout(() => setCopied(false), 3000);
  };

  const stats = analytics?.stats;

  return (
    <div className="space-y-8" data-testid="affiliation-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3">
          <Users className="text-[#00E676]" size={40} />
          AFFILIATION
        </h1>
        <p className="text-muted-foreground mt-2 uppercase tracking-wider text-sm">
          Gagnez de l'argent en recommandant Profimy Margin
        </p>
      </div>

      {/* How it works */}
      <div className="neo-card p-6">
        <h2 className="text-base md:text-lg font-bold uppercase tracking-tight mb-6 flex items-center gap-2">
          <Gift className="text-[#00E676]" size={22} />
          Comment ça marche ?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 border-2 border-[#0A0A0A] dark:border-[#00E676]/50">
            <div className="mx-auto w-14 h-14 bg-[#00E676] border-2 border-[#0A0A0A] flex items-center justify-center mb-4">
              <Share2 className="text-[#0A0A0A]" size={24} />
            </div>
            <h3 className="font-bold text-lg mb-2">1. Inscrivez-vous</h3>
            <p className="text-sm text-muted-foreground">
              Remplissez le formulaire et recevez votre lien affilié unique
            </p>
          </div>
          <div className="text-center p-6 border-2 border-[#0A0A0A] dark:border-[#00E676]/50">
            <div className="mx-auto w-14 h-14 bg-[#FFCC00] border-2 border-[#0A0A0A] flex items-center justify-center mb-4">
              <Link className="text-[#0A0A0A]" size={24} />
            </div>
            <h3 className="font-bold text-lg mb-2">2. Partagez</h3>
            <p className="text-sm text-muted-foreground">
              Partagez votre lien avec vos amis, votre communauté ou vos réseaux
            </p>
          </div>
          <div className="text-center p-6 border-2 border-[#0A0A0A] dark:border-[#00E676]/50">
            <div className="mx-auto w-14 h-14 bg-[#0A0A0A] border-2 border-[#0A0A0A] flex items-center justify-center mb-4">
              <DollarSign className="text-[#00E676]" size={24} />
            </div>
            <h3 className="font-bold text-lg mb-2">3. Gagnez 20%</h3>
            <p className="text-sm text-muted-foreground">
              Touchez 3,80€ pour chaque personne qui s'abonne via votre lien
            </p>
          </div>
        </div>
      </div>

      {/* Commission highlight */}
      <div className="bg-[#0A0A0A] text-white p-6 border-2 border-[#0A0A0A]">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-wider text-[#00E676] font-bold">Commission par vente</p>
            <p className="text-5xl font-extrabold text-[#00E676] mt-1">3,80€</p>
            <p className="text-xs text-white/50 mt-1">20% de 19€/mois</p>
          </div>
          <div className="text-center md:text-right">
            <p className="text-sm text-white/70">
              Paiements récurrents, sans limite de filleuls
            </p>
            <p className="text-lg font-bold mt-1">Lien unique — Suivi en temps réel</p>
          </div>
        </div>
      </div>

      {/* Registration / Dashboard */}
      {!affiliate ? (
        <div className="neo-card p-8 max-w-xl mx-auto" data-testid="affiliate-form">
          <h2 className="text-base md:text-lg font-bold uppercase tracking-tight mb-6 text-center">
            Rejoindre le programme
          </h2>
          <form onSubmit={handleRegister} className="space-y-5">
            <div>
              <label className="text-sm font-bold uppercase tracking-wider block mb-2">
                Nom complet
              </label>
              <Input
                type="text"
                placeholder="Votre nom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="neo-input w-full"
                data-testid="affiliate-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-bold uppercase tracking-wider block mb-2">
                Adresse email
              </label>
              <Input
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="neo-input w-full"
                data-testid="affiliate-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !name.trim() || !email.trim()}
              className="neo-button-primary w-full flex items-center justify-center gap-2"
              data-testid="affiliate-submit-btn"
            >
              {submitting ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <Users size={20} />
              )}
              Obtenir mon lien affilié
            </button>
          </form>
        </div>
      ) : (
        <>
          {/* Affiliate Link Card */}
          <div className="neo-card p-8" data-testid="affiliate-result">
            <div className="flex items-center gap-4 mb-6">
              <CheckCircle className="text-[#00E676] flex-shrink-0" size={32} />
              <div>
                <h2 className="text-xl font-bold">Bienvenue, {affiliate.name} !</h2>
                <p className="text-muted-foreground text-sm">Votre lien affilié est actif</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-sm font-bold uppercase tracking-wider block mb-2">
                Votre lien affilié
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={affiliate.referral_link}
                  readOnly
                  className="neo-input w-full font-mono text-sm"
                  data-testid="affiliate-link"
                />
                <button
                  onClick={copyLink}
                  className={`neo-button-${copied ? "primary" : "secondary"} flex items-center gap-2 px-4`}
                  data-testid="copy-link-btn"
                >
                  {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                  {copied ? "Copié" : "Copier"}
                </button>
              </div>
            </div>
          </div>

          {/* Analytics Dashboard */}
          <div data-testid="affiliate-analytics">
            <h2 className="text-base md:text-lg font-bold uppercase tracking-tight mb-6 flex items-center gap-2">
              <BarChart3 className="text-[#00E676]" size={22} />
              Tableau de bord Analytics
            </h2>

            {loadingAnalytics ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-[#00E676]" size={32} />
              </div>
            ) : (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="neo-card p-5">
                    <MousePointerClick className="text-blue-500 mb-2" size={22} />
                    <p className="text-3xl font-extrabold">{stats?.total_clicks || affiliate.clicks || 0}</p>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
                      Clics
                    </p>
                  </div>
                  <div className="neo-card p-5">
                    <TrendingUp className="text-[#00E676] mb-2" size={22} />
                    <p className="text-3xl font-extrabold">{stats?.total_conversions || affiliate.conversions || 0}</p>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
                      Conversions
                    </p>
                  </div>
                  <div className="neo-card p-5">
                    <BarChart3 className="text-[#FFCC00] mb-2" size={22} />
                    <p className="text-3xl font-extrabold">{stats?.conversion_rate || 0}%</p>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
                      Taux conversion
                    </p>
                  </div>
                  <div className="neo-card p-5">
                    <Wallet className="text-[#00E676] mb-2" size={22} />
                    <p className="text-3xl font-extrabold text-[#00E676]">{stats?.total_earnings || affiliate.earnings?.toFixed(2) || "0.00"}€</p>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
                      Gains totaux
                    </p>
                  </div>
                </div>

                {/* Earnings Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="neo-card p-6">
                    <h3 className="font-bold uppercase tracking-wider text-sm mb-4">Détail des gains</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 border-2 border-[#0A0A0A] dark:border-[#00E676]/30">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-[#00E676]"></div>
                          <span className="text-sm font-bold">Gains confirmés</span>
                        </div>
                        <span className="font-bold text-[#00E676]">{stats?.paid_earnings || 0}€</span>
                      </div>
                      <div className="flex justify-between items-center p-3 border-2 border-[#0A0A0A] dark:border-[#00E676]/30">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-[#FFCC00]"></div>
                          <span className="text-sm font-bold">En attente</span>
                        </div>
                        <span className="font-bold text-[#FFCC00]">{stats?.pending_earnings || 0}€</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-[#0A0A0A] text-white">
                        <span className="text-sm font-bold">Commission / vente</span>
                        <span className="font-bold text-[#00E676]">{stats?.commission_per_sale || 3.80}€</span>
                      </div>
                    </div>
                  </div>

                  <div className="neo-card p-6">
                    <h3 className="font-bold uppercase tracking-wider text-sm mb-4">Activité récente</h3>
                    {analytics?.daily_stats?.length > 0 ? (
                      <div className="space-y-2">
                        {analytics.daily_stats.slice(-7).reverse().map((day, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 border-b border-[#0A0A0A]/10 dark:border-white/10 text-sm">
                            <span className="text-muted-foreground">{new Date(day.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-blue-500 font-bold">{day.clicks} clics</span>
                              <span className="text-[#00E676] font-bold">{day.conversions} conv.</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="mx-auto text-muted-foreground mb-2" size={32} />
                        <p className="text-sm text-muted-foreground">
                          Partagez votre lien pour commencer à générer des statistiques
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recent Conversions */}
                {analytics?.recent_conversions?.length > 0 && (
                  <div className="neo-card p-6">
                    <h3 className="font-bold uppercase tracking-wider text-sm mb-4">Dernières conversions</h3>
                    <div className="space-y-2">
                      {analytics.recent_conversions.map((conv, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border-2 border-[#0A0A0A] dark:border-[#00E676]/30 text-sm">
                          <div>
                            <span className="font-bold">Nouveau filleul</span>
                            <span className="text-muted-foreground ml-2">
                              {new Date(conv.timestamp).toLocaleDateString("fr-FR")}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-0.5 text-xs font-bold uppercase ${
                              conv.status === "paid" ? "bg-[#00E676] text-[#0A0A0A]" : "bg-[#FFCC00] text-[#0A0A0A]"
                            }`}>
                              {conv.status === "paid" ? "Payé" : "En attente"}
                            </span>
                            <span className="font-bold text-[#00E676]">+{conv.commission?.toFixed(2) || "3.80"}€</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <button
            onClick={() => { setAffiliate(null); setAnalytics(null); setName(""); setEmail(""); }}
            className="neo-button-secondary text-sm"
          >
            Créer un autre compte affilié
          </button>
        </>
      )}
    </div>
  );
}
